import { ImageSizeOptions } from "./types";

import { AssetResponse } from "@/utils/FetchAssetDetails/types";
import { ImageType } from "@/utils/shared-atom-types";

/**
 * Formats an image URL to include size dimensions and quality parameters.
 *
 * @param url - The original image URL from the API.
 * @param options - Optional configuration for image dimensions and quality:
 *   - width: The desired image width (default: 452)
 *   - height: The desired image height (default: 602)
 *   - quality: The image quality level (default: 7)
 * @returns The formatted URL with size dimensions and quality parameters added.
 *
 * @example
 * ```typescript
 * const originalUrl = "https://imageservice.disco.peacocktv.com/uuid/94adade4-4de1-3316-b12f-bf7b3e5dbfb5/COVER_TITLE_TALL?language=eng&proposition=NBCUOTT&version=6650cda4-e490-3941-80f1-ceea266dfffe";
 * const formattedUrl = formatImageUrl(originalUrl);
 * console.log(formattedUrl); // Outputs: "https://imageservice.disco.peacocktv.com/uuid/94adade4-4de1-3316-b12f-bf7b3e5dbfb5/COVER_TITLE_TALL/452x602?image-quality=7&image-format=webp&language=eng&proposition=NBCUOTT&version=6650cda4-e490-3941-80f1-ceea266dfffe"
 *
 * // Custom dimensions and quality
 * const customUrl = formatImageUrl(originalUrl, { width: 600, height: 800, quality: 9 });
 * console.log(customUrl); // Outputs URL with /600x800?image-quality=9...
 * ```
 */
const formatImageUrl = (
  url: string | undefined,
  options?: {
    width?: number;
    height?: number;
    quality?: number;
  },
): string | undefined => {
  if (!url) return url;

  const { width = 452, height = 602, quality = 7 } = options || {};

  // Check if this is an imageservice.disco.peacocktv.com URL
  if (url.includes("imageservice.disco.peacocktv.com")) {
    // Split the URL at the query string
    const urlParts = url.split("?");
    const baseUrl = urlParts[0];
    const queryString = urlParts[1] || "";

    // Add the dimensions to the base URL
    const formattedBaseUrl = `${baseUrl}/${width}x${height}`;

    // Add the image quality and format parameters
    const additionalParams = `image-quality=${quality}&image-format=webp`;

    // Combine everything back together
    return `${formattedBaseUrl}?${additionalParams}${queryString ? "&" + queryString : ""}`;
  }

  return url;
};

/**
 * Searches an asset's images for an image of the given type.
 *
 * @param asset - the AssetResponse object containing details about the asset
 * @param imageType - the type of image to return
 * @param options - Optional configuration for image dimensions and quality
 * @returns the matched image URL or undefined if not found
 *
 * @example
 * ```typescript
 * getImageUrl(asset, ImageType.TitleArt34, { width: 300, height: 400, quality: 7 })
 * ```
 */
const getImageUrl = (
  asset: AssetResponse,
  imageType: ImageType,
  options?: ImageSizeOptions,
): string | undefined => {
  if (!("images" in asset.attributes)) {
    return undefined;
  }

  const image = asset.attributes.images.find((img) => img.type === imageType);

  if (!image) {
    return undefined;
  }

  return formatImageUrl(image.url, options);
};

export { getImageUrl, formatImageUrl };
